export interface PasswordValidationResult {
  isValid: boolean;
  errors: string[];
  strength: 'weak' | 'medium' | 'strong' | 'very-strong';
  requirements: PasswordRequirement[];
}

export interface PasswordRequirement {
  label: string;
  met: boolean;
  regex?: RegExp;
  validator?: (password: string) => boolean;
}

export const getPasswordRequirements = (password: string): PasswordRequirement[] => {
  return [
    {
      label: 'At least 8 characters',
      met: password.length >= 8,
      validator: (pwd) => pwd.length >= 8,
    },
    {
      label: 'One uppercase letter',
      met: /[A-Z]/.test(password),
      regex: /[A-Z]/,
    },
    {
      label: 'One lowercase letter',
      met: /[a-z]/.test(password),
      regex: /[a-z]/,
    },
    {
      label: 'One number',
      met: /[0-9]/.test(password),
      regex: /[0-9]/,
    },
    {
      label: 'One special character (!@#$%^&*)',
      met: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password),
      regex: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/,
    },
  ];
};

export const validatePassword = (password: string): PasswordValidationResult => {
  const requirements = getPasswordRequirements(password);
  const errors: string[] = [];

  requirements.forEach((req) => {
    if (!req.met) {
      errors.push(req.label);
    }
  });

  const metCount = requirements.filter((req) => req.met).length;
  let strength: 'weak' | 'medium' | 'strong' | 'very-strong' = 'weak';

  if (metCount === requirements.length) {
    if (password.length >= 12) {
      strength = 'very-strong';
    } else {
      strength = 'strong';
    }
  } else if (metCount >= 3) {
    strength = 'medium';
  } else {
    strength = 'weak';
  }

  return {
    isValid: errors.length === 0,
    errors,
    strength,
    requirements,
  };
};

export const getPasswordStrengthColor = (strength: string): string => {
  switch (strength) {
    case 'weak':
      return 'text-red-600 bg-red-100';
    case 'medium':
      return 'text-amber-600 bg-amber-100';
    case 'strong':
      return 'text-blue-600 bg-blue-100';
    case 'very-strong':
      return 'text-emerald-600 bg-emerald-100';
    default:
      return 'text-gray-600 bg-gray-100';
  }
};

export const getPasswordStrengthText = (strength: string): string => {
  switch (strength) {
    case 'weak':
      return 'Weak password';
    case 'medium':
      return 'Medium strength';
    case 'strong':
      return 'Strong password';
    case 'very-strong':
      return 'Very strong password';
    default:
      return '';
  }
};
